import React, { useEffect, useRef } from "react";
import { useForm, Controller } from "react-hook-form";
import styles from "./index.module.css";
import { labels } from "_utils/labels";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Link,
  Modal,
  TextField,
  Typography,
  IconButton,
} from "@mui/material";
import { logo } from "images";
import Grid from "@mui/material/Grid2";
import { materialsymbolsupload, materialsymbolsdownload, Delete } from "images";
import { useDispatch, useSelector } from "react-redux";
import { ERR_MSG } from "constants/labels";
import { alertActions } from "_store";
import {
  postReviewMapRequestApi,
  postReviewMapRequestStdUserApi,
} from "services/mapCenter/mapCenter.thunks";
import { mapRequestFormat } from "_utils/constant";

const ReviewModal = ({ open, setOpen, mapReqId, mapReqDetails }) => {
  const header = "Map Approval";
  const dispatch = useDispatch();
  const fileInputRef = useRef(null);
  const authUser = useSelector((x) => x.auth?.value);
  const user = authUser?.Data;
  const userAccess = user?.UserAccess;
  const isAdmin = userAccess?.some((access) =>
    access.Role.toLowerCase().includes("admin")
  );

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors, isValid },
  } = useForm({
    mode: "onBlur",
    defaultValues: {
      MapRequestId: mapReqId || 0,
      FileData: null,
      FileName: null,
      Comments: null,
      IsSendEmail: true,
      EmailTo: "",
      EmailSubject: "Your Map Center Request",
      EmailComments: "",
    },
  });

  useEffect(() => {
    if (mapReqDetails && mapReqDetails[0]?.EmailAddress) {
      setValue("EmailTo", mapReqDetails[0].EmailAddress);
    }
  }, [mapReqDetails, setValue, open]);

  const watchIsSendEmail = watch("IsSendEmail");
  const watchComments = watch("Comments");
  const watchFileName = watch("FileName");
  const watchFileData = watch("FileData");
  const watchEmailTo = watch("EmailTo");
  const watchEmailSubject = watch("EmailSubject");
  const watchEmailComments = watch("EmailComments");

  const isFormValid = () => {
    if (!isAdmin) {
      return watchComments || watchFileName;
    } else if (watchIsSendEmail) {
      return watchEmailTo && watchEmailSubject && watchEmailComments;
    } else {
      return watchComments || watchFileName;
    }
  };

  const MAX_FILE_SIZE_MB = isAdmin ? 500 : 250;
  const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

  const handleError = (error) => {
    dispatch(
      alertActions.error({
        message: error.message,
        header,
      })
    );
    if (fileInputRef?.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleFileUpload = async (event) => {
    dispatch(alertActions.clear());
    event.preventDefault();

    const filesAdded = event.dataTransfer
      ? event.dataTransfer.files
      : event.target.files;

    if (!filesAdded || filesAdded.length === 0) return;

    const file = filesAdded[0];

    const fileExtension = file.name
      .toLowerCase()
      .substring(file.name.lastIndexOf("."));

    const isValidFormat = mapRequestFormat.some(
      (format) =>
        fileExtension === format.toLowerCase() ||
        (format === ".tar.gz" && file.name.toLowerCase().endsWith(".tar.gz"))
    );

    if (!isValidFormat) {
      handleError({
        code: "ERR_MSG_049",
        message: "Invalid file format.",
      });
      return;
    }

    if (file.size > MAX_FILE_SIZE_BYTES) {
      handleError({
        code: "ERR_MSG_050",
        message: `File size cannot exceed ${MAX_FILE_SIZE_MB} MB`,
      });
      return;
    }

    if (file.size === 0) {
      handleError({
        code: "ERR_FILE_EMPTY",
        message: "File is empty",
      });
      return;
    }

    setValue("FileData", file);
    setValue("FileName", file.name);

    if (fileInputRef?.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleDownloadFile = () => {
    if (!watchFileData) return;

    const url = URL.createObjectURL(watchFileData);

    const link = document.createElement("a");
    link.href = url;
    link.download = watchFileName;
    document.body.appendChild(link);
    link.click();

    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleDeleteFile = () => {
    setValue("FileData", "");
    setValue("FileName", "");
  };

  const onSubmit = async (data) => {
    try {
      const formData = new FormData();

      formData.append("MapRequestId", mapReqId);
      formData.append("Comments", data.Comments ?? "");
      if (data.FileData) {
        formData.append("FileData", data.FileData);
        formData.append("FileName", data.FileName);
      }
      if (isAdmin) {
        formData.append("IsSendEmail", data.IsSendEmail);
        formData.append("EmailTo", data.EmailTo ?? "");
        formData.append("EmailSubject", data.EmailSubject ?? "");
        formData.append("EmailComments", data.EmailComments ?? "");
      }

      const res = await dispatch(
        isAdmin
          ? postReviewMapRequestApi(formData)
          : postReviewMapRequestStdUserApi(formData)
      ).unwrap();

      if (!res || res?.Succeeded === false) {
        dispatch(
          alertActions.error({
            message: res?.Message || ERR_MSG["073"],
            header: header,
          })
        );
        handleClose();
        return;
      }
      dispatch(
        alertActions.success({
          message: res?.Message,
          header: header,
        })
      );
      handleClose();
    } catch (error) {
      dispatch(
        alertActions.error({
          message: error.message ? ERR_MSG["073"] : error,
          header: header,
        })
      );
      handleClose();
    }
  };

  const handleClose = () => {
    reset();
    setOpen(false);
  };

  useEffect(() => {
    if (!watchIsSendEmail) {
      setValue("EmailTo", null);
      setValue("EmailSubject", null);
      setValue("EmailComments", null);
    } else {
      if (!watchEmailSubject)
        setValue("EmailSubject", "Your Map Center Request");
      if (mapReqDetails && mapReqDetails[0]?.EmailAddress) {
        setValue("EmailTo", mapReqDetails[0].EmailAddress);
      }
    }
  }, [watchIsSendEmail, setValue, watchEmailSubject, mapReqDetails]);

  return (
    <>
      <Modal
        open={open}
        aria-labelledby="child-modal-title"
        aria-describedby="child-modal-description"
        className={styles.modalContainer}
      >
        <Box className={styles.modalBox}>
          <Box className={styles.modalBoxInner}>
            <Link href="#" variant="logo" className="wgllogo">
              <img src={logo} alt="logo"></img>
              {labels.eServicePortal}
            </Link>
            <h5 className="headercontent">
              <b>{header}</b>
            </h5>

            <form onSubmit={handleSubmit(onSubmit)}>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 12, md: 6 }}>
                  <Controller
                    name="Comments"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        multiline
                        rows={9}
                        fullWidth
                        variant="outlined"
                        placeholder="Leave your comment"
                        error={!!errors.Comments}
                        helperText={errors.Comments?.message}
                      />
                    )}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 12, md: 6 }}>
                  <Typography className={styles.customerName}>
                    DOCUMENT UPLOAD
                  </Typography>
                  <Typography
                    component="div"
                    sx={{ flexDirection: "column" }}
                    className="UploadContainer"
                  >
                    <Button
                      component="label"
                      variant="contained"
                      className="Uploadfiles"
                      startIcon={
                        <img src={materialsymbolsupload} alt="Upload" />
                      }
                    >
                      <span>Upload your files here</span>
                      <input type="file" hidden onChange={handleFileUpload} />
                      <input type="file" hidden onChange={handleFileUpload} />
                    </Button>
                    {watchFileName && (
                      <Box
                        sx={{
                          mt: 1,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                      >
                        <Typography variant="body2">
                          Selected file: {watchFileName}
                        </Typography>
                        <Box sx={{ display: "flex", gap: 0.5 }}>
                          <IconButton size="small" onClick={handleDownloadFile}>
                            <img
                              src={materialsymbolsdownload}
                              alt="Download"
                              style={{ width: 20, height: 20 }}
                            />
                          </IconButton>
                          <IconButton size="small" onClick={handleDeleteFile}>
                            <img
                              src={Delete}
                              alt="Delete"
                              style={{ width: 20, height: 20 }}
                            />
                          </IconButton>
                        </Box>
                      </Box>
                    )}
                    <Typography component="div" className="SupportedFormats">
                      <Typography component="h3">Supported Formats</Typography>
                      <Typography component="div" className="fileformatlist">
                        {mapRequestFormat &&
                          mapRequestFormat.map((format) => (
                            <span
                              className={styles.mapRequestFileFormat}
                              key={format}
                            >
                              {format}
                            </span>
                          ))}
                      </Typography>
                    </Typography>
                  </Typography>
                </Grid>
              </Grid>
              {isAdmin && (
                <>
                  <Box>
                    <Controller
                      name="IsSendEmail"
                      control={control}
                      render={({ field: { onChange, value } }) => (
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={value}
                              onChange={(e) => onChange(e.target.checked)}
                            />
                          }
                          label="Send Email"
                        />
                      )}
                    />
                  </Box>

                  <Grid container spacing={1}>
                    <Grid size={{ xs: 12, sm: 12, md: 3 }}>
                      <Typography>From : </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 12, md: 9 }}>
                      <Typography
                        sx={{ fontWeight: "700", marginBottom: "16px" }}
                      >
                        Washington Gas Map Service
                      </Typography>
                    </Grid>
                  </Grid>
                  <Grid container spacing={1}>
                    <Grid size={{ xs: 12, sm: 12, md: 3 }}>
                      <Typography>To : </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 12, md: 9 }}>
                      <Controller
                        name="EmailTo"
                        control={control}
                        rules={{
                          required: watchIsSendEmail
                            ? "Email To is required"
                            : false,
                          pattern: {
                            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                            message: "Please enter a valid email address",
                          },
                        }}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            sx={{ marginBottom: "16px" }}
                            fullWidth
                            size="small"
                            disabled={true}
                            error={!!errors.EmailTo}
                            helperText={errors.EmailTo?.message}
                          />
                        )}
                      />
                    </Grid>
                  </Grid>
                  <Grid container spacing={1}>
                    <Grid size={{ xs: 12, sm: 12, md: 3 }}>
                      <Typography>Subject : </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 12, md: 9 }}>
                      <Controller
                        name="EmailSubject"
                        control={control}
                        rules={{
                          required: watchIsSendEmail
                            ? "Subject is required"
                            : false,
                        }}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            sx={{ marginBottom: "16px" }}
                            fullWidth
                            size="small"
                            disabled={!watchIsSendEmail}
                            error={watchIsSendEmail && !!errors.EmailSubject}
                            helperText={
                              watchIsSendEmail && errors.EmailSubject?.message
                            }
                          />
                        )}
                      />
                    </Grid>
                  </Grid>
                  <Grid container spacing={1}>
                    <Grid size={{ xs: 12, sm: 12, md: 3 }}>
                      <Typography>Message : </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 12, md: 9 }}>
                      <Controller
                        name="EmailComments"
                        control={control}
                        rules={{
                          required: watchIsSendEmail
                            ? "Email comments are required"
                            : false,
                        }}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            sx={{ marginBottom: "16px" }}
                            fullWidth
                            multiline
                            rows={3}
                            size="small"
                            disabled={!watchIsSendEmail}
                            error={watchIsSendEmail && !!errors.EmailComments}
                            helperText={
                              watchIsSendEmail && errors.EmailComments?.message
                            }
                          />
                        )}
                      />
                    </Grid>
                  </Grid>
                </>
              )}

              <Button
                type="submit"
                fullWidth
                variant="contained"
                color="primary"
                className={styles.submitButton}
                disabled={!isFormValid()}
              >
                SUBMIT
              </Button>
            </form>

            <Button
              fullWidth
              variant="contained"
              color="secondary"
              className={styles.closeButton}
              onClick={handleClose}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default ReviewModal;
